import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { GoogleAuthDebug } from '../components/Auth/GoogleAuthDebug';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
/**
 * Google Auth Test Page
 * Use this page to test Google OAuth integration
 * Add to AppRouter.tsx: <Route path="/test-google" element={<GoogleAuthTestPage />} />
 */
export function GoogleAuthTestPage() {
  const {
    user,
    signInWithGoogle,
    signOut
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign-in error:', error);
      setLoading(false);
    }
  };
  const handleSignOut = async () => {
    await signOut();
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-4101">
      <header className="bg-white border-b border-gray-200" data-id="element-4102">
        <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-4103">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-4104">
            <ArrowLeft size={16} className="mr-1" data-id="element-4105" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-4106">
        <div className="w-full max-w-2xl" data-id="element-4107">
          <div className="bg-white rounded-xl shadow-sm p-8 mb-6" data-id="element-4108">
            <h1 className="text-3xl font-bold mb-2" data-id="element-4109">
              🧪 Google OAuth Test Page
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-4110">
              Use this page to test your Google Sign-In integration
            </p>

            {/* Current Status */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6" data-id="element-4111">
              <h2 className="font-semibold mb-3" data-id="element-4112">Current Status</h2>
              <div className="space-y-2 text-sm" data-id="element-4113">
                <div className="flex items-center justify-between" data-id="element-4114">
                  <span data-id="element-4115">Authentication:</span>
                  <span className={`font-medium ${user ? 'text-green-600' : 'text-red-600'}`} data-id="element-4116">
                    {user ? '✅ Logged In' : '❌ Not Logged In'}
                  </span>
                </div>
                {user && <>
                    <div className="flex items-center justify-between" data-id="element-4117">
                      <span data-id="element-4118">Email:</span>
                      <span className="font-medium" data-id="element-4119">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4120">
                      <span data-id="element-4121">Name:</span>
                      <span className="font-medium" data-id="element-4122">
                        {user.user_metadata?.fullName || 'Not set'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4123">
                      <span data-id="element-4124">Provider:</span>
                      <span className="font-medium" data-id="element-4125">
                        {user.app_metadata?.provider || 'Unknown'}
                      </span>
                    </div>
                  </>}
              </div>
            </div>

            {/* Test Actions */}
            <div className="space-y-4" data-id="element-4126">
              {!user ? <>
                  <h2 className="font-semibold mb-3" data-id="element-4127">Test Google Sign-In</h2>
                  <GoogleSignInButton onClick={handleGoogleSignIn} loading={loading} text="Test Google Sign-In" data-id="element-4128" />
                  <p className="text-xs text-gray-500 text-center" data-id="element-4129">
                    Click the button above to test the Google OAuth flow
                  </p>
                </> : <>
                  <h2 className="font-semibold mb-3" data-id="element-4130">
                    Logged In Successfully!
                  </h2>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-id="element-4131">
                    <p className="text-sm text-green-800" data-id="element-4132">
                      ✅ Google Sign-In is working correctly!
                    </p>
                  </div>
                  <button onClick={handleSignOut} className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-4133">
                    Sign Out
                  </button>
                </>}
            </div>

            {/* Setup Checklist */}
            <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-4134">
              <h2 className="font-semibold mb-3" data-id="element-4135">Setup Checklist</h2>
              <div className="space-y-2 text-sm" data-id="element-4136">
                <label className="flex items-start" data-id="element-4137">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4138" />
                  <span data-id="element-4139">Enabled Google provider in Supabase Dashboard</span>
                </label>
                <label className="flex items-start" data-id="element-4140">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4141" />
                  <span data-id="element-4142">Added OAuth Client ID and Secret in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-4143">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4144" />
                  <span data-id="element-4145">Verified redirect URL in Google Cloud Console</span>
                </label>
                <label className="flex items-start" data-id="element-4146">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4147" />
                  <span data-id="element-4148">Disabled "Skip nonce checks" in Supabase</span>
                </label>
                <label className="flex items-start" data-id="element-4149">
                  <input type="checkbox" className="mt-1 mr-2" data-id="element-4150" />
                  <span data-id="element-4151">Disabled "Allow users without email" in Supabase</span>
                </label>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-4152">
              <h2 className="font-semibold mb-3" data-id="element-4153">Quick Links</h2>
              <div className="space-y-2" data-id="element-4154">
                <a href="https://supabase.com/dashboard/project/neadfoxscgjeekvabnoz/auth/providers" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-4155">
                  → Supabase Auth Providers Dashboard
                </a>
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-600 hover:underline" data-id="element-4156">
                  → Google Cloud Console Credentials
                </a>
                <Link to="/signup" className="block text-sm text-purple-600 hover:underline" data-id="element-4157">
                  → Go to Signup Page
                </Link>
                <Link to="/login" className="block text-sm text-purple-600 hover:underline" data-id="element-4158">
                  → Go to Login Page
                </Link>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6" data-id="element-4159">
            <h3 className="font-semibold text-blue-900 mb-3" data-id="element-4160">
              📋 Testing Instructions
            </h3>
            <ol className="space-y-2 text-sm text-blue-800" data-id="element-4161">
              <li data-id="element-4162">1. Complete the setup checklist above</li>
              <li data-id="element-4163">2. Click "Test Google Sign-In" button</li>
              <li data-id="element-4164">3. You'll be redirected to Google OAuth</li>
              <li data-id="element-4165">4. Authorize the app</li>
              <li data-id="element-4166">5. You'll be redirected back (to /onboarding)</li>
              <li data-id="element-4167">6. Check that you're logged in</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      <GoogleAuthDebug data-id="element-4168" />
    </div>;
}